import React, { useEffect, useMemo, useState } from "react";
import { TrendUp, TrendDown, Target } from "@phosphor-icons/react";
import { PlayerTag } from "./badges";
import { getThreatDashboard } from "../lib/api";
import RangeFilter, { passesRange } from "./RangeFilter";

const RISK_COLOR = {
    HIGH: "#FF2A2A",
    MEDIUM: "#FFF3C2",
    LOW: "#A1A1AA",
};

function PerfBar({ diff }) {
    // Center-pivot bar: negative fills left in red, positive fills right in green.
    const max = 12;
    const pct = Math.min(Math.abs(diff) / max, 1) * 50; // max half width
    const color = diff >= 0 ? "#9EE8B4" : "#FF2A2A";
    return (
        <div className="relative h-1 w-full bg-white/5">
            <div
                className="absolute top-0 h-1"
                style={{
                    left: diff >= 0 ? "50%" : `${50 - pct}%`,
                    width: `${pct}%`,
                    background: color,
                }}
            />
            <div className="absolute left-1/2 top-0 h-1 w-px bg-white/40" />
        </div>
    );
}

function ThreatRow({ player, direction }) {
    const colorSign = direction === "over" ? "#9EE8B4" : "#FF2A2A";
    return (
        <div
            className="bg-black/60 border border-white/5 p-2"
            data-testid={`threat-row-${player.id}`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[10px] text-white/40 uppercase">
                        {player.team}
                    </span>
                    <span className="font-display font-bold text-sm uppercase truncate">
                        {player.name}
                    </span>
                    <span className="font-mono text-[10px] text-white/40">
                        {player.position}
                    </span>
                </div>
                <span
                    className="font-mono text-[9px] uppercase tracking-widest font-bold leading-none px-1 py-0.5 border"
                    style={{
                        color: RISK_COLOR[player.sustainabilityRisk] || "#A1A1AA",
                        borderColor: `${RISK_COLOR[player.sustainabilityRisk]}60`,
                    }}
                >
                    {player.sustainabilityRisk}
                </span>
            </div>
            <div className="grid grid-cols-[auto_auto_auto_1fr] gap-2 items-center mt-1.5 text-[10px] font-mono tabular-nums text-white/60">
                <span>
                    G <span className="text-white/90 font-bold">{player.goals}</span>
                </span>
                <span>
                    xG <span className="text-white/90 font-bold">{player.expectedGoals}</span>
                </span>
                <span
                    className="font-bold"
                    style={{ color: colorSign }}
                >
                    Δ {player.threatPerformance > 0 ? "+" : ""}
                    {player.threatPerformance}
                </span>
                <PerfBar diff={player.threatPerformance} />
            </div>
            {player.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                    {player.tags.map((t) => (
                        <PlayerTag key={t} tag={t} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function ThreatDashboardSection({ window: gwWindow = 3 }) {
    const [data, setData] = useState(null);
    const [deltaRange, setDeltaRange] = useState({ selectedMin: -12, selectedMax: 12 });

    useEffect(() => {
        getThreatDashboard(gwWindow)
            .then(setData)
            .catch(() => {});
    }, [gwWindow]);

    const filter = useMemo(
        () => ({ ...deltaRange, isEnabled: true }),
        [deltaRange],
    );

    const filteredOver = useMemo(
        () => (data?.overperformers || []).filter((p) => passesRange(p.threatPerformance, filter)),
        [data, filter],
    );
    const filteredUnder = useMemo(
        () => (data?.underperformers || []).filter((p) => passesRange(p.threatPerformance, filter)),
        [data, filter],
    );

    if (!data) return null;

    return (
        <section>
            <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
                <div>
                    <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight flex items-center gap-2">
                        <Target size={22} weight="fill" />
                        Threat vs Goals
                    </h2>
                    <p className="text-white/60 text-sm">
                        Goals minus expected from FPL threat · relevant MID/FWD only · {data.total_relevant} players
                    </p>
                </div>
                <div className="w-full md:w-80">
                    <RangeFilter
                        id="threat-delta"
                        testid="threat-delta-filter"
                        label="Delta (G − xG)"
                        minValue={-12}
                        maxValue={12}
                        step={1}
                        selectedMin={deltaRange.selectedMin}
                        selectedMax={deltaRange.selectedMax}
                        format="number"
                        onChange={setDeltaRange}
                        defaults={{ selectedMin: -12, selectedMax: 12 }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-[#121212] border border-white/10 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5 text-fa-green">
                        <TrendUp size={14} weight="bold" />
                        Overperformers ({filteredOver.length}{filteredOver.length !== data.overperformers.length ? ` of ${data.overperformers.length}` : ""})
                    </div>
                    <div className="space-y-2" data-testid="threat-overperformers">
                        {filteredOver.length ? (
                            filteredOver.map((p) => (
                                <ThreatRow key={p.id} player={p} direction="over" />
                            ))
                        ) : (
                            <div className="text-white/30 text-xs italic">None match the current filter</div>
                        )}
                    </div>
                </div>

                <div className="bg-[#121212] border border-white/10 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5 text-fa-red">
                        <TrendDown size={14} weight="bold" />
                        Underperformers ({filteredUnder.length}{filteredUnder.length !== data.underperformers.length ? ` of ${data.underperformers.length}` : ""})
                    </div>
                    <div className="space-y-2" data-testid="threat-underperformers">
                        {filteredUnder.length ? (
                            filteredUnder.map((p) => (
                                <ThreatRow key={p.id} player={p} direction="under" />
                            ))
                        ) : (
                            <div className="text-white/30 text-xs italic">None match the current filter</div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
